import { MetricData } from '@/features/centerReview';
import React, { useCallback, useMemo } from 'react';
import Table from '@/components/Table/Table';
import { createColumnHelper } from '@tanstack/react-table';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { colors } from '@/lib/colors';
import { Col, Row } from '@/lib/utils';
import SvgIcon from '@/components/SvgIcon';
import { CodeValueType } from './ModalAllReview';
import NewProfileImage from '@/components/NewProfileImage';
import { TooltipWrapper } from '@/components/TooltipWrapper';

type Props = {
    metricData: MetricData[];
    selectedQuestionCode: CodeValueType;
    label: string;
    activeTab: 'all' | 'positive' | 'negative' | 'custom';
    selectedAnswerCode: CodeValueType | null;
};

function ModalAllReviewTable({ metricData, selectedQuestionCode, label, activeTab, selectedAnswerCode }: Props) {
    const questionPrefix = label ? `${label} · ${selectedQuestionCode.value}`.trim() : selectedQuestionCode.value;
    const selectedAnswerTone = selectedAnswerCode?.code.toLowerCase().includes('neg')
        ? 'negative'
        : selectedAnswerCode?.code.toLowerCase().includes('pos')
          ? 'positive'
          : null;
    const renderHeader = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (text: string, column?: any) => (
            <div
                className="flex items-center justify-center gap-[4px] cursor-pointer select-none"
                onClick={column?.getToggleSortingHandler?.()}
            >
                <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                    {text}
                </RegularText>
            </div>
        ),
        [],
    );
    // 케이스 1: 질문 O / 반응 카테고리 X / 답변 X
    const case1Columns = useMemo(() => {
        const columnHelper = createColumnHelper<MetricData>();
        return [
            columnHelper.display({
                id: 'rank',
                header: ({ column }) => renderHeader('순위', column),
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={21} color={colors.Text_1} letterSpacing={-0.3}>
                        {info.row.index + 1}
                    </RegularText>
                ),
                size: 112,
            }),
            columnHelper.accessor('coachProfileImageUrl', {
                id: 'coachProfile',
                header: ({ column }) => renderHeader('강사', column),
                cell: (info) => (
                    <Row className="w-full px-[20px] items-center gap-[12px] ">
                        <NewProfileImage
                            imageUrl={info.getValue()}
                            size={38}
                            circleColor={'#F2F4F6'}
                            imageFill="#D2D8DE"
                        />
                        <RegularText fontSize={16} lineHeight={21} color={colors.Text_1} letterSpacing={-0.3}>
                            {info.row.original.coachName}
                        </RegularText>
                    </Row>
                ),
                size: 171,
                enableSorting: false,
            }),
            columnHelper.accessor('coachQuestionAverageRating', {
                header: ({ column }) => renderHeader('수업 시간 평균 만족도', column),
                cell: (info) => (
                    <TooltipWrapper
                        tooltip={{
                            opacity: 1,
                            customContent: (
                                <Row>
                                    <MediumText fontSize={13} lineHeight={18} color={colors.white} className="">
                                        {info.row.original.coachQuestionMemberCount.toLocaleString()}명
                                    </MediumText>
                                    <DemiLightText fontSize={13} lineHeight={18} color={colors.white}>
                                        {' 회원/'}
                                    </DemiLightText>
                                    <MediumText fontSize={13} lineHeight={18} color={colors.white} className="">
                                        {info.row.original.coachQuestionAnswerCount.toLocaleString()}개
                                    </MediumText>
                                    <DemiLightText fontSize={13} lineHeight={18} color={colors.white}>
                                        {' 의견'}
                                    </DemiLightText>
                                </Row>
                            ),
                        }}
                    >
                        <Row className="items-center gap-[8px]">
                            <SvgIcon name="SvgStar" fill="#3D73DD" size={9} />
                            <RegularText fontSize={16} lineHeight={21} color={colors.Text_1} letterSpacing={-0.3}>
                                {info.getValue().toFixed(1)}
                            </RegularText>
                        </Row>
                    </TooltipWrapper>
                ),
                size: 191,
            }),
            columnHelper.display({
                id: 'satisfactionRatio',
                header: ({ column }) => {
                    return (
                        <div
                            className="flex items-center justify-center gap-[4px] cursor-pointer select-none"
                            onClick={column.getToggleSortingHandler()}
                        >
                            <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                                좋았어요/아쉬웠어요 비율
                            </RegularText>
                            <TooltipWrapper
                                tooltip={{
                                    opacity: 1,
                                    customContent: (
                                        <DemiLightText fontSize={13} lineHeight={18} color={colors.white}>
                                            {`이 강사가 받은 수업 시간 관련 반응의 비율이에요`}
                                        </DemiLightText>
                                    ),
                                }}
                            >
                                <SvgIcon name="SvgWarningInCircle" size={17} fill={'#B4BCC4'} className="ml-[8px]" />
                            </TooltipWrapper>
                        </div>
                    );
                },
                cell: (info) => {
                    const positiveRate = info.row.original.coachQuestionPositiveAnswerRate;
                    const negativeRate = info.row.original.coachQuestionNegativeAnswerRate;
                    return (
                        <Col className="gap-[2px]">
                            <Row className="items-center gap-[10px]">
                                <div className="relative w-[120px] h-[3px] bg-[#F1F2F4] rounded-full overflow-hidden">
                                    <div
                                        className="absolute left-0 top-0 h-full bg-[#59D696]"
                                        style={{ width: `${Math.min(Math.max(positiveRate, 0), 100)}%` }}
                                    />
                                    <div
                                        className="absolute right-0 top-0 h-full bg-[#FF999B]"
                                        style={{ width: `${Math.min(Math.max(negativeRate, 0), 100)}%` }}
                                    />
                                </div>
                            </Row>
                            <Row className="items-center justify-between w-[120px]">
                                <RegularText fontSize={16} lineHeight={21} color="#00A793">
                                    {Math.round(positiveRate)}
                                </RegularText>
                                <RegularText fontSize={16} lineHeight={21} color="#FF553E">
                                    {Math.round(negativeRate)}
                                </RegularText>
                            </Row>
                        </Col>
                    );
                },
                size: 271,
            }),
            columnHelper.accessor('coachStageAverageRating', {
                header: ({ column }) => renderHeader('전체 평균 만족도', column),
                cell: (info) => (
                    <Row className="items-center gap-[8px]">
                        <SvgIcon name="SvgStar" fill="#3D73DD" size={9} />
                        <RegularText fontSize={16} lineHeight={21} color={colors.Text_1} letterSpacing={-0.3}>
                            {info.getValue().toFixed(1)}
                        </RegularText>
                    </Row>
                ),
                size: 171,
                enableSorting: false,
            }),
        ];
    }, [renderHeader]);

    // 케이스 2: 질문 O / 반응 카테고리 O / 답변 X
    const case2Columns = useMemo(() => {
        const columnHelper = createColumnHelper<MetricData>();
        const isPositive = activeTab === 'positive';

        return [
            columnHelper.display({
                id: 'rank',
                header: ({ column }) => renderHeader('순위', column),
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={21} color={colors.Text_1} letterSpacing={-0.3}>
                        {info.row.index + 1}
                    </RegularText>
                ),
                size: 70,
            }),
            columnHelper.accessor('coachProfileImageUrl', {
                id: 'coachProfile',
                header: ({ column }) => renderHeader('강사', column),
                cell: (info) => (
                    <Row className="w-full px-[20px] items-center gap-[12px] ">
                        <NewProfileImage
                            imageUrl={info.getValue()}
                            size={38}
                            circleColor={'#F2F4F6'}
                            imageFill="#D2D8DE"
                        />
                        <RegularText fontSize={16} lineHeight={21} color={colors.Text_1} letterSpacing={-0.3}>
                            {info.row.original.coachName}
                        </RegularText>
                    </Row>
                ),
                size: 150,
                enableSorting: false,
            }),
            columnHelper.accessor(
                isPositive ? 'coachQuestionPositiveAnswerCount' : 'coachQuestionNegativeAnswerCount',
                {
                    header: ({ column }) => renderHeader(`${isPositive ? '좋았어요' : '아쉬웠어요'} 의견 횟수`, column),
                    cell: (info) => (
                        <RegularText fontSize={16} lineHeight={21} color={colors.Text_1} letterSpacing={-0.3}>
                            {info.getValue().toLocaleString()}
                        </RegularText>
                    ),
                    size: 191,
                },
            ),
            columnHelper.display({
                id: 'satisfactionRatio',
                header: ({ column }) => (
                    <div
                        className="flex items-center justify-center gap-[4px] cursor-pointer select-none"
                        onClick={column.getToggleSortingHandler()}
                    >
                        <RegularText fontSize={16} lineHeight={20} color={colors.Text_1}>
                            {`${isPositive ? '좋았어요' : '아쉬웠어요'} 의견 비율`}
                        </RegularText>
                    </div>
                ),
                cell: (info) => {
                    const rate = isPositive
                        ? info.row.original.coachQuestionPositiveAnswerRate
                        : info.row.original.coachQuestionNegativeAnswerRate;
                    return (
                        <Row className="items-center gap-[10px]">
                            <div className="relative w-[120px] h-[3px] bg-[#F1F2F4] rounded-full overflow-hidden">
                                <div
                                    className="absolute left-0 top-0 h-full"
                                    style={{
                                        width: `${Math.min(Math.max(rate, 0), 100)}%`,
                                        backgroundColor: isPositive ? '#59D696' : '#FF999B',
                                    }}
                                />
                            </div>
                            <MediumText fontSize={16} lineHeight={21} color={isPositive ? '#00A793' : '#FF553E'}>
                                {Math.round(rate)}
                            </MediumText>
                        </Row>
                    );
                },
                size: 271,
            }),
            columnHelper.accessor('coachQuestionAverageRating', {
                header: ({ column }) => renderHeader('수업 시간 평균 만족도', column),
                cell: (info) => (
                    <Row className="items-center gap-[8px]">
                        <SvgIcon name="SvgStar" fill="#3D73DD" size={9} />
                        <RegularText fontSize={16} lineHeight={21} color={colors.Text_1}>
                            {info.getValue().toFixed(1)}
                        </RegularText>
                    </Row>
                ),
                size: 171,
                enableSorting: false,
            }),
        ];
    }, [activeTab, renderHeader]);

    // 케이스 3: 질문 O / 반응 카테고리 O or X / 답변 O
    const case3Columns = useMemo(() => {
        const columnHelper = createColumnHelper<MetricData & { answerCount: number }>();

        return [
            columnHelper.display({
                id: 'rank',
                header: ({ column }) => renderHeader('순위', column),
                cell: (info) => (
                    <RegularText fontSize={16} lineHeight={21} color={'#191F28'} letterSpacing={-0.3}>
                        {info.row.index + 1}
                    </RegularText>
                ),
                size: 112,
            }),
            columnHelper.accessor('coachProfileImageUrl', {
                id: 'coachProfile',
                header: ({ column }) => renderHeader('강사', column),
                cell: (info) => (
                    <Row className="w-full px-[20px] items-center gap-[12px] ">
                        <NewProfileImage
                            imageUrl={info.getValue()}
                            size={38}
                            circleColor={'#F2F4F6'}
                            imageFill="#D2D8DE"
                        />
                        <RegularText fontSize={16} lineHeight={21} color={'#191F28'} letterSpacing={-0.3}>
                            {info.row.original.coachName}
                        </RegularText>
                    </Row>
                ),
                size: 171,
                enableSorting: false,
            }),
            columnHelper.accessor('answerCount', {
                header: ({ column }) => renderHeader('의견 받은 횟수', column),
                cell: (info) => (
                    <MediumText fontSize={16} lineHeight={21} color={colors.Text_1}>
                        {info.getValue().toLocaleString()}회
                    </MediumText>
                ),
                size: 191,
            }),
            columnHelper.accessor('coachQuestionAverageRating', {
                header: ({ column }) => renderHeader('수업 시간 평균 만족도', column),
                cell: (info) => (
                    <Row className="items-center gap-[8px]">
                        <SvgIcon name="SvgStar" fill="#3D73DD" size={9} />
                        <RegularText fontSize={16} lineHeight={21} color={colors.Text_1}>
                            {info.getValue().toFixed(1)}
                        </RegularText>
                    </Row>
                ),
                size: 271,
            }),
            columnHelper.accessor('coachStageAverageRating', {
                header: ({ column }) => renderHeader('전체 평균 만족도', column),
                cell: (info) => (
                    <Row className="items-center gap-[8px]">
                        <SvgIcon name="SvgStar" fill="#3D73DD" size={9} />
                        <RegularText fontSize={16} lineHeight={21} color={colors.Text_1}>
                            {info.getValue().toFixed(1)}
                        </RegularText>
                    </Row>
                ),
                size: 171,
            }),
        ];
    }, [renderHeader]);

    // 데이터 처리
    const { tableData, columns } = useMemo(() => {
        // 케이스 3: 답변이 선택된 경우
        if (selectedAnswerCode) {
            const mapKey = `${selectedQuestionCode.code}-${selectedAnswerCode.code}`;
            const processedData = metricData
                .map((coach) => ({
                    ...coach,
                    answerCount: coach.codeValueAndAnswerCountMap.get(mapKey) || 0,
                }))
                .sort((a, b) => b.answerCount - a.answerCount);

            return {
                tableData: processedData,
                columns: case3Columns,
            };
        }

        // 케이스 2: 긍정/부정 반응 선택
        if (activeTab === 'positive' || activeTab === 'negative') {
            const sortedData = [...metricData].sort(
                (a, b) => b.coachQuestionAverageRating - a.coachQuestionAverageRating,
            );
            return {
                tableData: sortedData,
                columns: case2Columns,
            };
        }

        // 케이스 1: 기본 (모든 반응)
        const sortedData = [...metricData].sort((a, b) => b.coachQuestionAverageRating - a.coachQuestionAverageRating);
        return {
            tableData: sortedData,
            columns: case1Columns,
        };
    }, [activeTab, selectedAnswerCode, selectedQuestionCode, metricData, case1Columns, case2Columns, case3Columns]);

    // activeTab이 custom일 때는 테이블을 표시하지 않음
    if (activeTab === 'custom') {
        return null;
    }

    // 테이블 헤더 텍스트 생성
    const getTableHeaderText = () => {
        if (selectedAnswerCode) {
            return {
                mainText: selectedAnswerCode.value,
                subText: '를 선택한 강사',
            };
        }

        if (activeTab === 'all') {
            return {
                mainText: questionPrefix,
                subText: '에 대한 강사 만족도 비교',
            };
        }

        if (activeTab === 'positive' || activeTab === 'negative') {
            const tabLabel = activeTab === 'positive' ? '좋았어요' : '아쉬웠어요';
            return {
                mainText: `${selectedQuestionCode.value}에서 '${tabLabel}'`,
                subText: ' 의견을 많이 받은 강사',
            };
        }

        return null;
    };

    const headerText = getTableHeaderText();

    return (
        <Col>
            {headerText && (
                <Row className="mb-[24px]">
                    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <div className="relative inline-block">
                            <MediumText
                                fontSize={20}
                                lineHeight={30}
                                color={colors.Text_1}
                                className="relative z-50"
                                letterSpacing={-0.7}
                            >
                                {headerText.mainText}
                            </MediumText>
                            <Col
                                className={` h-[4px] w-full absolute bottom-[3px] left-0 z-10 ${
                                    selectedAnswerTone === 'positive' || activeTab === 'positive'
                                        ? 'bg-[#C7ECDA]'
                                        : selectedAnswerTone === 'negative' || activeTab === 'negative'
                                          ? 'bg-[#FEEBEC]'
                                          : 'bg-[#CEDEFF]'
                                }`}
                            />
                        </div>
                        <DemiLightText fontSize={20} lineHeight={30} color={colors.Text_1} letterSpacing={-0.7}>
                            {headerText.subText}
                        </DemiLightText>
                    </div>
                </Row>
            )}
            <Table
                data={tableData}
                columns={columns}
                height="400px"
                headerColor="white"
                isUnballancedRowColor
                rowHeight={66}
                headerAndCellPadding={60}
            />
        </Col>
    );
}

export default ModalAllReviewTable;
